import { useState, useEffect } from 'react';
import { useQueryClient } from '@tanstack/react-query';
// DTC-K: route the vehicle `updated_at` lifecycle write through the
// canonical clock so the ticket-mutation audit timestamp is
// deterministic under `clock.setFixed(...)`. The L114 `getFullYear()`
// year-picker is SAFE-A display and intentionally preserved.
import { clock } from '@/utils/clock';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Separator } from '@/components/ui/separator';
import { supabase } from '@/integrations/supabase/client';
import { decodeVinOnServer, updateVehicle } from '@/lib/api/vehicleApi';
import { toast } from '@/hooks/use-toast';
import { Loader2, User, Car } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { notifyDayboardInvalidated } from '@/utils/dayboardInvalidation';
// Core-100 R4 X4 (CORE-AE D/S): the ticket's customer fields are written through the governed server path.
import { editTicketCustomerContact } from '@/lib/tickets/governedTicketEdits';

interface EditCustomerVehicleModalProps {
  isOpen: boolean;
  onClose: () => void;
  ticketId: string;
  // Customer fields
  initialName?: string;
  initialPhone?: string;
  initialEmail?: string;
  emailOptIn?: boolean;
  smsOptIn?: boolean;
  preferredChannel?: string;
  fallbackChannel?: string;
  // Vehicle fields
  initialYear?: string;
  initialMake?: string;
  initialModel?: string;
  initialLicensePlate?: string;
  initialVin?: string;
  initialEngineSize?: string;
  initialOdometerMiles?: number;
  initialGvwrLbs?: number;
}

const PACKAGE_MAPPING: Record<string, string> = {
  'asian': 'Asian',
  'domestic': 'Domestic',
  'light_duty_diesel': 'Diesel',
  'diesel': 'Diesel',
  'euro': 'Euro',
  'ev': 'EV',
  'exotic': 'Exotic',
};
const DEFAULT_PACKAGES = ['Asian', 'Domestic', 'Euro', 'EV', 'Diesel', 'Exotic'];

export const EditCustomerVehicleModal = ({
  isOpen,
  onClose,
  ticketId,
  initialName = '',
  initialPhone = '',
  initialEmail = '',
  emailOptIn = true,
  smsOptIn = false,
  preferredChannel = 'email',
  fallbackChannel = 'sms',
  initialYear = '',
  initialMake = '',
  initialModel = '',
  initialLicensePlate = '',
  initialVin = '',
  initialEngineSize = '',
  initialOdometerMiles,
  initialGvwrLbs,
}: EditCustomerVehicleModalProps) => {
  const { activeShop } = useAuth();
  const queryClient = useQueryClient();

  // Customer state
  const [customerName, setCustomerName] = useState(initialName);
  const [customerPhone, setCustomerPhone] = useState(initialPhone);
  const [customerEmail, setCustomerEmail] = useState(initialEmail);
  const [emailOpt, setEmailOpt] = useState(emailOptIn);
  const [smsOpt, setSmsOpt] = useState(smsOptIn);
  const [preferred, setPreferred] = useState(preferredChannel);
  const [fallback, setFallback] = useState(fallbackChannel);

  // Vehicle state
  const [year, setYear] = useState(initialYear);
  const [make, setMake] = useState(initialMake);
  const [model, setModel] = useState(initialModel);
  const [engineSize, setEngineSize] = useState(initialEngineSize);
  const [licensePlate, setLicensePlate] = useState(initialLicensePlate);
  const [vin, setVin] = useState(initialVin);
  const [odometerMiles, setOdometerMiles] = useState(initialOdometerMiles?.toString() ?? '');
  const [gvwrLbs, setGvwrLbs] = useState(initialGvwrLbs?.toString() ?? '');

  // Catalog dropdown state
  const [availableMakes, setAvailableMakes] = useState<string[]>([]);
  const [availableModels, setAvailableModels] = useState<string[]>([]);
  const [availableEngines, setAvailableEngines] = useState<string[]>([]);
  const [isLoadingMakes, setIsLoadingMakes] = useState(false);
  const [isLoadingModels, setIsLoadingModels] = useState(false);
  const [isLoadingEngines, setIsLoadingEngines] = useState(false);
  const [catalogPackages, setCatalogPackages] = useState<string[]>(DEFAULT_PACKAGES);

  const [isSaving, setIsSaving] = useState(false);
  const [isDecoding, setIsDecoding] = useState(false);
  const [isInitialLoad, setIsInitialLoad] = useState(true);

  // Generate year range
  const currentYear = new Date().getFullYear();
  const years = Array.from({ length: currentYear - 1989 }, (_, i) => currentYear + 1 - i);

  // Fetch shop vehicle packages on open
  useEffect(() => {
    if (!isOpen || !activeShop?.id) return;
    const fetchPackages = async () => {
      const { data } = await supabase
        .from('shop_profile')
        .select('vehicle_packages')
        .eq('shop_id', activeShop.id)
        .single();
      if (data?.vehicle_packages && Array.isArray(data.vehicle_packages)) {
        const mapped = (data.vehicle_packages as string[]).map(id => PACKAGE_MAPPING[id]).filter(Boolean);
        setCatalogPackages(mapped.length > 0 ? mapped : DEFAULT_PACKAGES);
      }
    };
    fetchPackages();
  }, [isOpen, activeShop?.id]);

  // Reset form state when modal opens
  useEffect(() => {
    if (isOpen) {
      setCustomerName(initialName);
      setCustomerPhone(initialPhone);
      setCustomerEmail(initialEmail);
      setEmailOpt(emailOptIn);
      setSmsOpt(smsOptIn);
      setPreferred(preferredChannel);
      setFallback(fallbackChannel);
      setYear(initialYear);
      setMake(initialMake);
      setModel(initialModel);
      setEngineSize(initialEngineSize);
      setLicensePlate(initialLicensePlate);
      setVin(initialVin);
      setOdometerMiles(initialOdometerMiles?.toString() ?? '');
      setGvwrLbs(initialGvwrLbs?.toString() ?? '');
      setIsInitialLoad(true);
    }
  }, [isOpen, initialName, initialPhone, initialEmail, emailOptIn, smsOptIn, preferredChannel, fallbackChannel, initialYear, initialMake, initialModel, initialEngineSize, initialLicensePlate, initialVin, initialOdometerMiles, initialGvwrLbs]);

  // Fetch makes when year changes
  useEffect(() => {
    if (!year) {
      setAvailableMakes([]);
      return;
    }
    const fetchMakes = async () => {
      setIsLoadingMakes(true);
      try {
        const { data, error } = await supabase.functions.invoke('get-vehicle-makes-catalog', {
          body: { year: parseInt(year), packageTypes: catalogPackages },
        });
        if (error) throw error;
        const makes: string[] = data?.makes || [];
        // Keep current make visible even if not in catalog
        if (make && !makes.includes(make)) makes.push(make);
        setAvailableMakes(makes);
      } catch (err) {
        console.error('Error fetching makes:', err);
        setAvailableMakes(make ? [make] : []);
      } finally {
        setIsLoadingMakes(false);
      }
    };
    fetchMakes();
  }, [year, catalogPackages]);

  // Fetch models when make changes
  useEffect(() => {
    if (!year || !make) {
      setAvailableModels([]);
      return;
    }
    const fetchModels = async () => {
      setIsLoadingModels(true);
      try {
        const { data, error } = await supabase.functions.invoke('get-vehicle-models-catalog', {
          body: { make, year: parseInt(year), packageTypes: catalogPackages },
        });
        if (error) throw error;
        const models: string[] = data?.models || [];
        if (model && !models.includes(model)) models.push(model);
        setAvailableModels(models);
      } catch (err) {
        console.error('Error fetching models:', err);
        setAvailableModels(model ? [model] : []);
      } finally {
        setIsLoadingModels(false);
      }
    };
    fetchModels();
  }, [year, make, catalogPackages]);

  // Fetch engines when model changes
  useEffect(() => {
    if (!year || !make || !model) {
      setAvailableEngines([]);
      return;
    }
    let cancelled = false;
    const fetchEngines = async () => {
      setIsLoadingEngines(true);
      try {
        const { data, error } = await supabase.functions.invoke('get-vehicle-engines-catalog', {
          body: { make, model, year: parseInt(year), packageTypes: catalogPackages },
        });
        if (cancelled) return;
        if (error) throw error;
        const engines: string[] = data?.engines || [];
        if (engineSize && !engines.includes(engineSize)) engines.push(engineSize);
        setAvailableEngines(engines);
        // Mark initial load complete after first engine fetch
        if (isInitialLoad) setIsInitialLoad(false);
      } catch (err) {
        if (cancelled) return;
        console.error('Error fetching engines:', err);
        setAvailableEngines(engineSize ? [engineSize] : []);
        if (isInitialLoad) setIsInitialLoad(false);
      } finally {
        if (!cancelled) setIsLoadingEngines(false);
      }
    };
    fetchEngines();
    return () => { cancelled = true; };
  }, [year, make, model, catalogPackages]);

  const handleYearChange = (value: string) => {
    setYear(value);
    if (!isInitialLoad) {
      setMake('');
      setModel('');
      setEngineSize('');
      setAvailableModels([]);
      setAvailableEngines([]);
    }
  };

  const handleMakeChange = (value: string) => {
    setMake(value);
    if (!isInitialLoad) {
      setModel('');
      setEngineSize('');
      setAvailableEngines([]);
    }
  };

  const handleModelChange = (value: string) => {
    setModel(value);
    if (!isInitialLoad) {
      setEngineSize('');
    }
  };

  const handleDecodeVin = async () => {
    if (!vin || vin.length !== 17) {
      toast({
        title: 'Invalid VIN',
        description: 'VIN must be exactly 17 characters',
        variant: 'destructive',
      });
      return;
    }

    setIsDecoding(true);
    try {
      // OptiVehicle Profile: the trusted server decodes (one NHTSA request, the
      // canonical mapping) and leaves a receipt, so saving this VIN does not ask
      // NHTSA again. The browser never calls NHTSA.
      const decoded = activeShop?.id ? await decodeVinOnServer(activeShop.id, vin) : null;
      if (!decoded) {
        toast({ title: 'Not decoded', description: 'NHTSA returned no build data for this VIN.' });
      } else {
        const b = decoded.legacyBasics;
        // Set values - keep initial load true so cascading doesn't clear
        if (b.year !== undefined) setYear(String(b.year));
        if (b.make) setMake(b.make);
        if (b.model) setModel(b.model);
        if (b.displacement) {
          const liters = parseFloat(parseFloat(b.displacement).toFixed(1));
          if (!isNaN(liters) && liters > 0) setEngineSize(`${liters}L`);
        }
        // GVWR is never filled from the decoder: it is the human-confirmed lift-safety value.

        toast({
          title: 'VIN Decoded',
          description: 'Vehicle information populated successfully',
        });
      }
    } catch (error) {
      console.error('Error decoding VIN:', error);
      toast({
        title: 'Error',
        description: 'Failed to decode VIN',
        variant: 'destructive',
      });
    } finally {
      setIsDecoding(false);
    }
  };

  const handleSave = async () => {
    setIsSaving(true);
    try {
      // Step 1: Update vehicle FIRST so that when the ticket update triggers
      // the realtime subscription refetch, the vehicle join data is already fresh.
      if (year && make && model) {
        const { data: ticketData, error: fetchError } = await supabase
          .from('tickets')
          .select('vehicle_id, shop_id')
          .eq('id', ticketId)
          .single();

        if (fetchError) throw fetchError;

        if (ticketData?.vehicle_id) {
          const gvwrValue = gvwrLbs ? parseInt(gvwrLbs) : null;
          await updateVehicle({
            vehicle_id: ticketData.vehicle_id,
            // Core-100 R4 X4: vehicle-write REQUIRES the shop (400 "shop_id required") — the empty string sent here made
            // every Save with year/make/model fail before the customer fields were written. The ticket's own shop.
            shop_id: (ticketData as { shop_id?: string }).shop_id ?? '',
            year: parseInt(year),
            make,
            model,
            license_plate: licensePlate || null,
            vin: vin || null,
            engine_size: engineSize || null,
            gvwr_lbs: (gvwrValue && !isNaN(gvwrValue) && gvwrValue > 0) ? gvwrValue : null,
          });
        }
      }

      // Step 2: Update customer info on ticket LAST — this triggers the realtime
      // subscription which refetches ticket + joined vehicle data.
      const customerUpdates: any = {
        customer_name: customerName.trim() || null,
        customer_phone: customerPhone.trim() || null,
        customer_email: customerEmail.trim() || null,
        email_opt_in: emailOpt,
        sms_opt_in: smsOpt,
        preferred_channel: preferred,
        fallback_channel: fallback,
        // Force updated_at so realtime always fires even if no customer fields changed
        updated_at: clock.now().toISOString(),
      };

      // Update odometer and GVWR snapshot on ticket if provided
      if (odometerMiles) {
        const odometerValue = parseInt(odometerMiles);
        if (!isNaN(odometerValue) && odometerValue > 0) {
          customerUpdates.odometer_miles = odometerValue;
        }
      }
      // Update THIS ticket's GVWR snapshot only
      const gvwrValue = gvwrLbs ? parseInt(gvwrLbs) : null;
      if (gvwrValue && !isNaN(gvwrValue) && gvwrValue > 0) {
        customerUpdates.gvwr_lbs = gvwrValue;
      }

      // Core-100 R4 X4 (CORE-AE D/S): edit_ticket_customer_contact — front office only, these fields only,
      // audited by field name; a browser UPDATE of tickets is no longer this modal's path.
      const saved = await editTicketCustomerContact(ticketId, customerUpdates);
      if (!saved.ok) throw new Error(saved.error);

      toast({
        title: 'Updated Successfully',
        description: 'Customer and vehicle information saved',
      });

      // Invalidate queries so UI updates immediately
      queryClient.invalidateQueries({ queryKey: ['tickets'] });
      queryClient.invalidateQueries({ queryKey: ['customer-vehicles'] });
      queryClient.invalidateQueries({ queryKey: ['customer'] });

      // Cross-device Dayboard sync — covers customer-edit, vehicle-edit,
      // and Command Center ticket edits routed through this modal.
      void notifyDayboardInvalidated({
        reason: 'ticket-edit',
        shopId: activeShop?.id || '',
        ticketId,
      });

      onClose();
    } catch (error: any) {
      // supabase.functions.invoke() on a 4xx/5xx surfaces `error.message`
      // as the generic "Edge Function returned a non-2xx status code".
      // The actual server reason lives in `error.context.response` (the
      // underlying Response object). Surface it so the operator sees the
      // specific failure ("Invalid phone format" / "Permission denied" /
      // "Customer not found" / etc.) instead of the generic wrapper.
      // Also console.error the raw error for DevTools inspection.
      console.error('[EditCustomerVehicleModal] save failed:', error);
      let description = error?.message || 'Failed to save changes';
      try {
        const respBody = await (error?.context?.response as Response | undefined)?.clone()?.json();
        const serverMsg = respBody?.error || respBody?.message || respBody?.reason;
        if (serverMsg && typeof serverMsg === 'string') {
          description = serverMsg;
        }
      } catch {
        // Response body not JSON or already consumed — fall back to error.message.
      }
      toast({
        title: 'Error',
        description,
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[560px] max-h-[85vh]">
        <DialogHeader>
          <DialogTitle>Edit Customer & Vehicle</DialogTitle>
          <DialogDescription>
            Update customer details and vehicle information for this ticket
          </DialogDescription>
        </DialogHeader>

        <ScrollArea className="max-h-[calc(85vh-180px)] pr-4">
          <div className="space-y-6 py-4">
            {/* Customer Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Customer</h3>
              </div>

              <div className="space-y-2">
                <Label htmlFor="cv-customerName">Customer Name</Label>
                <Input
                  id="cv-customerName"
                  value={customerName}
                  onChange={(e) => setCustomerName(e.target.value)}
                  placeholder="Enter customer name"
                />
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="cv-customerPhone">Phone</Label>
                  <Input
                    id="cv-customerPhone"
                    type="tel"
                    value={customerPhone}
                    onChange={(e) => setCustomerPhone(e.target.value)}
                    placeholder="+1234567890"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cv-customerEmail">Email</Label>
                  <Input
                    id="cv-customerEmail"
                    type="email"
                    value={customerEmail}
                    onChange={(e) => setCustomerEmail(e.target.value)}
                    placeholder="customer@example.com"
                  />
                </div>
              </div>

              <div className="space-y-3">
                <Label className="text-sm font-medium">Communication Preferences</Label>
                <div className="flex items-center gap-6">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="cv-email-opt"
                      checked={emailOpt}
                      onCheckedChange={(checked) => setEmailOpt(checked === true)}
                    />
                    <Label htmlFor="cv-email-opt" className="text-sm font-normal cursor-pointer">Email opt-in</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="cv-sms-opt"
                      checked={smsOpt}
                      onCheckedChange={(checked) => setSmsOpt(checked === true)}
                    />
                    <Label htmlFor="cv-sms-opt" className="text-sm font-normal cursor-pointer">
                      SMS consent
                    </Label>
                  </div>
                </div>
                <p className="text-xs text-muted-foreground">
                  By enabling SMS, the customer agrees to receive transactional messages
                  related to appointments, service updates, and repair notifications.
                  Message frequency varies. Msg &amp; data rates may apply.
                  Reply STOP to unsubscribe.
                </p>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="cv-preferred">Preferred Channel</Label>
                  <Select value={preferred} onValueChange={setPreferred}>
                    <SelectTrigger id="cv-preferred">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="sms">SMS</SelectItem>
                      <SelectItem value="none">None</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cv-fallback">Fallback Channel</Label>
                  <Select value={fallback} onValueChange={setFallback}>
                    <SelectTrigger id="cv-fallback">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="email">Email</SelectItem>
                      <SelectItem value="sms">SMS</SelectItem>
                      <SelectItem value="none">None</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <Separator />

            {/* Vehicle Section */}
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <Car className="h-4 w-4 text-muted-foreground" />
                <h3 className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Vehicle</h3>
              </div>

              <div className="space-y-2">
                <Label htmlFor="cv-vin">VIN</Label>
                <div className="flex gap-2">
                  <Input
                    id="cv-vin"
                    value={vin}
                    onChange={(e) => setVin(e.target.value.toUpperCase())}
                    placeholder="17-character VIN"
                    maxLength={17}
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleDecodeVin}
                    disabled={isDecoding || vin.length !== 17}
                  >
                    {isDecoding && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Decode
                  </Button>
                </div>
              </div>

              {/* Year */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Year *</Label>
                  <Select value={year} onValueChange={handleYearChange}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select year" />
                    </SelectTrigger>
                    <SelectContent>
                      {years.map(y => (
                        <SelectItem key={y} value={y.toString()}>{y}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Make */}
                <div className="space-y-2">
                  <Label>Make *</Label>
                  <Select value={make} onValueChange={handleMakeChange} disabled={!year}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select make" />
                    </SelectTrigger>
                    <SelectContent>
                      {isLoadingMakes ? (
                        <div className="p-2 text-center text-sm text-muted-foreground">Loading makes...</div>
                      ) : availableMakes.length === 0 ? (
                        <div className="p-2 text-center text-sm text-muted-foreground">No makes available</div>
                      ) : (
                        availableMakes.filter(m => m && m.trim() !== '').map(m => (
                          <SelectItem key={m} value={m}>{m}</SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Model & Engine */}
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label>Model *</Label>
                  <Select value={model} onValueChange={handleModelChange} disabled={!make}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select model" />
                    </SelectTrigger>
                    <SelectContent>
                      {isLoadingModels ? (
                        <div className="p-2 text-center text-sm text-muted-foreground">Loading models...</div>
                      ) : availableModels.length === 0 ? (
                        <div className="p-2 text-center text-sm text-muted-foreground">No models available</div>
                      ) : (
                        availableModels.filter(m => m && m.trim() !== '').map(m => (
                          <SelectItem key={m} value={m}>{m}</SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Engine Size</Label>
                  <Select value={engineSize} onValueChange={setEngineSize} disabled={!model}>
                    <SelectTrigger>
                      <SelectValue placeholder="Select engine" />
                    </SelectTrigger>
                    <SelectContent>
                      {isLoadingEngines ? (
                        <div className="p-2 text-center text-sm text-muted-foreground">Loading engines...</div>
                      ) : availableEngines.length === 0 ? (
                        <div className="p-2 text-center text-sm text-muted-foreground">No engines available</div>
                      ) : (
                        availableEngines.filter(e => e && e.trim() !== '').map(e => (
                          <SelectItem key={e} value={e}>{e}</SelectItem>
                        ))
                      )}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="cv-odometerMiles">Odometer (miles)</Label>
                  <Input
                    id="cv-odometerMiles"
                    type="number"
                    value={odometerMiles}
                    onChange={(e) => setOdometerMiles(e.target.value)}
                    placeholder="50000"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="cv-gvwrLbs">GVWR (lbs)</Label>
                  <Input
                    id="cv-gvwrLbs"
                    type="number"
                    value={gvwrLbs}
                    onChange={(e) => setGvwrLbs(e.target.value)}
                    placeholder="6000"
                  />
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="cv-licensePlate">License Plate</Label>
                  <Input
                    id="cv-licensePlate"
                    value={licensePlate}
                    onChange={(e) => setLicensePlate(e.target.value)}
                    placeholder="ABC123"
                  />
                </div>
              </div>
            </div>
          </div>
        </ScrollArea>

        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={isSaving}>
            Cancel
          </Button>
          <Button onClick={handleSave} disabled={isSaving}>
            {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isSaving ? 'Saving...' : 'Save Changes'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
